/**
 * useSidebarExpand.ts — Estado del rail lateral en desktop/tablet.
 *
 * Hover expand (ratón fino) + toggle manual (hamburguesa / táctil).
 * Exporta helpers; no renderiza UI.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

const hoverExpandQuery = "(hover: hover) and (pointer: fine)";
const lgQuery = "(min-width: 1024px)";
const coarsePointerQuery = "(pointer: coarse)";

type ExpandMode = "hover" | "manual" | null;

function mediaSubscribe(query: string, cb: () => void) {
  const mq = window.matchMedia(query);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
}

function mediaMatches(query: string) {
  return window.matchMedia(query).matches;
}

export function useSidebarExpand(options: {
  pathname: string;
  onExpandChange?: (expanded: boolean) => void;
}) {
  const { pathname, onExpandChange } = options;
  const [isExpanded, setIsExpanded] = useState(false);
  const expandModeRef = useRef<ExpandMode>(null);
  const onExpandChangeRef = useRef(onExpandChange);
  onExpandChangeRef.current = onExpandChange;

  const isLg = useSyncExternalStore(
    (cb) => mediaSubscribe(lgQuery, cb),
    () => mediaMatches(lgQuery),
    () => false,
  );

  const canHoverExpand = useSyncExternalStore(
    (cb) => mediaSubscribe(hoverExpandQuery, cb),
    () => mediaMatches(hoverExpandQuery),
    () => false,
  );

  const isCoarsePointer = useSyncExternalStore(
    (cb) => mediaSubscribe(coarsePointerQuery, cb),
    () => mediaMatches(coarsePointerQuery),
    () => false,
  );

  const applyExpanded = useCallback((next: boolean, mode: ExpandMode) => {
    expandModeRef.current = next ? mode : null;
    setIsExpanded(next);
    onExpandChangeRef.current?.(next);
  }, []);

  const setExpanded = useCallback(
    (next: boolean) => {
      applyExpanded(next, next ? "manual" : null);
    },
    [applyExpanded],
  );

  const expandByHover = useCallback(() => {
    applyExpanded(true, "hover");
  }, [applyExpanded]);

  const expandManually = useCallback(() => {
    applyExpanded(true, "manual");
  }, [applyExpanded]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => {
      const next = !prev;
      expandModeRef.current = next ? "manual" : null;
      onExpandChangeRef.current?.(next);
      return next;
    });
  }, []);

  const collapseExpanded = useCallback(() => {
    applyExpanded(false, null);
  }, [applyExpanded]);

  const collapseOnHoverLeave = useCallback(() => {
    if (expandModeRef.current === "hover") {
      applyExpanded(false, null);
    }
  }, [applyExpanded]);

  // Solo al cambiar de ruta — no depender de callbacks inestables del padre.
  useEffect(() => {
    expandModeRef.current = null;
    setIsExpanded(false);
    onExpandChangeRef.current?.(false);
  }, [pathname]);

  /** Backdrop táctil: tablet/desktop sin hover fino y rail expandido manualmente. */
  const showTouchBackdrop =
    isLg &&
    isExpanded &&
    expandModeRef.current === "manual" &&
    (!canHoverExpand || isCoarsePointer);

  return {
    isLg,
    canHoverExpand,
    isCoarsePointer,
    isExpanded,
    isCollapsedDesktop: isLg && !isExpanded,
    setExpanded,
    expandByHover,
    expandManually,
    toggleExpanded,
    collapseExpanded,
    collapseOnHoverLeave,
    showTouchBackdrop,
  };
}
